import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex } from 'components/ui';
import { colors, radii } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import basketConstants from 'constants/Basket';
import languageLocale from 'utils/LanguageLocale';
import FulfillmentCard from 'components/ProductPage/FulfillmentMethods/FulfillmentCard';
import FreeReturnsBanner from 'components/ProductPage/FulfillmentMethods/FreeReturnsBanner';
import ShippingDetailsPanel from 'components/ProductPage/FulfillmentMethods/ShippingDetailsPanel';
import AutoReplenishRow from 'components/ProductPage/FulfillmentMethods/AutoReplenishRow';
import useFulfillmentMethodsProps from 'components/ProductPage/FulfillmentMethods/hooks/useFulfillmentMethodsProps';
import { formatCardEta, formatPanelEta } from 'components/ProductPage/FulfillmentMethods/utils/formatEta';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { DELIVERY_OPTIONS } = basketConstants;
const getText = languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

const styles = {
    root: {
        backgroundColor: colors.nearWhite
    },
    divider: {
        height: 1,
        width: '100%',
        backgroundColor: colors.lightGray
    }
};

const FulfillmentMethods = ({ currentProduct, commerce }) => {
    // SHP-5111 delivers the visual starting point: Free Shipping is preselected;
    // Fast Delivery / Pickup selected states arrive in follow-up stories.
    const [selectedMethod, setSelectedMethod] = useState(DELIVERY_OPTIONS.STANDARD);

    const {
        preferredZipCode, shipToHomeMessage, showAutoReplenishment, isReplenishmentEligible, isGiftCard
    } = useFulfillmentMethodsProps(currentProduct);

    const currentSku = currentProduct?.currentSku;

    // Guards against a stale selection when the mounted SKU becomes a gift card
    // (e.g. a shade/variant change) while Fast Delivery/Pickup was selected —
    // those cards become disabled, so the selection must fall back to shipping.
    useEffect(() => {
        if (isGiftCard) {
            setSelectedMethod(DELIVERY_OPTIONS.STANDARD);
        }
    }, [isGiftCard]);

    const handleSelect = useCallback(method => () => setSelectedMethod(method), []);

    const shippingCardEta = formatCardEta(shipToHomeMessage);
    const panelEtaText = formatPanelEta(shipToHomeMessage);

    const cards = [
        {
            id: 'fulfillmentMethod-standard',
            method: DELIVERY_OPTIONS.STANDARD,
            iconName: 'shippingTruck',
            title: getText('freeShippingCardTitle'),
            etaText: shippingCardEta,
            etaColor: 'green'
        },
        {
            id: 'fulfillmentMethod-fastDelivery',
            method: DELIVERY_OPTIONS.SAME_DAY,
            iconName: 'bag',
            title: getText('fastDeliveryCardTitle'),
            disabled: isGiftCard,
            secondaryText: isGiftCard ? getText('fastDeliveryNotAvailable') : undefined,
            etaText: isGiftCard ? undefined : getText('fastDeliveryCardEta'),
            etaColor: 'green'
        },
        {
            id: 'fulfillmentMethod-pickup',
            method: DELIVERY_OPTIONS.PICKUP,
            iconName: 'store',
            title: isGiftCard ? getText('availableInAllStoresCardTitle') : getText('pickupCardTitle'),
            disabled: isGiftCard,
            secondaryText: isGiftCard ? getText('pickupNotAvailable') : undefined,
            etaText: isGiftCard ? undefined : getText('pickupCardEta'),
            etaColor: 'green'
        }
    ];

    return (
        <Box
            is='fieldset'
            marginTop={4}
            marginBottom={4}
            marginX={['-container', 0]}
            padding={4}
            borderRadius={radii[1]}
            lineHeight='tight'
            css={styles.root}
        >
            <Flex
                flexDirection='column'
                gap={3}
                width='100%'
            >
                <FreeReturnsBanner />
                <Flex
                    flexDirection='column'
                    gap={2}
                    width='100%'
                >
                    <Flex
                        role='radiogroup'
                        aria-label={getText('fulfillmentMethodsGroupAriaLabel')}
                        gap={2}
                        width='100%'
                        alignItems='stretch'
                    >
                        {cards.map(card => {
                            const secondary = card.secondaryText || card.etaText;
                            const ariaLabel = secondary ? `${card.title}, ${secondary}` : card.title;

                            return (
                                <Box
                                    key={card.id}
                                    display='flex'
                                    flex='1 1 0'
                                    minWidth={0}
                                >
                                    <FulfillmentCard
                                        id={card.id}
                                        iconName={card.iconName}
                                        title={card.title}
                                        etaText={card.etaText}
                                        etaColor={card.etaColor}
                                        secondaryText={card.secondaryText}
                                        disabled={card.disabled}
                                        isSelected={selectedMethod === card.method}
                                        onSelect={handleSelect(card.method)}
                                        ariaLabel={ariaLabel}
                                    />
                                </Box>
                            );
                        })}
                    </Flex>
                    <Box
                        padding={0}
                        borderRadius={radii[1]}
                        backgroundColor='white'
                    >
                        <Box padding={3}>
                            <ShippingDetailsPanel
                                etaText={panelEtaText}
                                zipCode={preferredZipCode}
                                currentSku={currentSku}
                            />
                            {showAutoReplenishment && (
                                <>
                                    <Box
                                        marginTop={2}
                                        marginBottom={2}
                                        css={styles.divider}
                                    />
                                    <AutoReplenishRow
                                        currentSku={currentSku}
                                        commerce={commerce}
                                        isReplenishmentEligible={isReplenishmentEligible}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                </Flex>
            </Flex>
        </Box>
    );
};

FulfillmentMethods.propTypes = {
    currentProduct: PropTypes.object,
    commerce: PropTypes.object
};

export default wrapFunctionalComponent(FulfillmentMethods, 'FulfillmentMethods');
